import { ChevronDown } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/common/components/ui/button';
import { ErrorDescriptionPanel } from '@/policy/component/Validation/ErrorDescriptionPanel';
import { ExtractDataCoherentButton } from '@/policy/component/Validation/ExtractDataCoherentButton';
import { ExtractedDataErrorButton } from '@/policy/component/Validation/ExtractedDataErrorButton';
import { ValidationResult } from '@/policy/types/ValidationResult';

interface ExtractionStatusIndicatorsProps {
  validation: ValidationResult;
  onCoherentClick: () => void;
  onErrorClick: () => void;
  onDescriptionChange: (description: string) => void;
  onCancel: () => void;
  onSave: () => void;
}

export const ExtractionStatusIndicators = ({
  validation,
  onCoherentClick,
  onErrorClick,
  onDescriptionChange,
  onCancel,
  onSave,
}: ExtractionStatusIndicatorsProps) => {
  const isCoherent = validation.status === 'COHERENT';
  const isError = validation.status === 'ERROR';
  const isConfirmed = isError && validation.confirmed;

  const [isExpanded, setIsExpanded] = useState(!isConfirmed);

  const handleCancel = () => {
    setIsExpanded(false);
    onCancel();
  };

  const handleSave = () => {
    setIsExpanded(false);
    onSave();
  };

  const renderErrorSection = () => {
    if (!isError) return null;

    if (isConfirmed && !isExpanded) {
      return (
        <div className="flex justify-center">
          <Button
            variant="ghost"
            size="sm"
            onClick={() => setIsExpanded(true)}
            className="w-full"
            aria-label="Expand error description"
          >
            <ChevronDown className="size-4" />
          </Button>
        </div>
      );
    }

    return (
      <ErrorDescriptionPanel
        description={validation.description}
        onDescriptionChange={onDescriptionChange}
        onCancel={handleCancel}
        onSave={handleSave}
      />
    );
  };

  return (
    <div className="space-y-2">
      <div className="flex justify-center items-center gap-0">
        <ExtractDataCoherentButton
          onClick={onCoherentClick}
          isCoherent={isCoherent}
        />
        <ExtractedDataErrorButton onClick={onErrorClick} isError={isError} />
      </div>
      {renderErrorSection()}
    </div>
  );
};
